import { useNavigate } from "react-router-dom";
import { useState } from "react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import "./LegalPage.css";

export default function SupportPage() {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      question: "How do I get started with OkeyBot?",
      answer: "Getting started is easy! First, add the bot to your Discord server using the invite link on our homepage. Then, log in to the dashboard with your Discord account, add your API keys from your chosen providers (OpenAI, Anthropic, etc.), and enable the models you want to use. You can also use OkeyBot’s built‑in models instead of connecting a key for certain providers. Finally, use /chat in any Discord channel to start your first conversation!"
    },
    {
      question: "Why isn't the bot responding to my messages?",
      answer: "There are a few common reasons: (1) Make sure you've added at least one API key in the dashboard, (2) Verify that you have at least one model enabled on the Models page, (3) Check that your API key is valid and your provider account has credits/billing set up, (4) Try using /setup in Discord to see if there are any configuration issues."
    },
    {
      question: "How do API keys work? Are they safe?",
      answer: "You maintain full control of your API keys. We store them encrypted at rest and only decrypt them when needed to fulfill your requests. Your keys are never shared with other users or third parties (except the providers you choose). You can remove or replace keys anytime in the dashboard. We never see or store your conversation content - messages live in Discord."
    },
    {
      question: "What's the difference between Free and Premium?",
      answer: "The Free plan gives you 20 messages per day and includes a small welcome grant of Okey Credits for built‑in models. Premium ($3.99/month or $2.99/month billed yearly) provides unlimited messages and refreshes your Okey Credits for built‑in models each billing period. Both plans let you use all supported providers with your own API keys, and you always pay provider costs directly to them. Plan limits apply whether you use your own keys or any built‑in models."
    },
    {
      question: "How are costs calculated?",
      answer: "OkeyBot doesn't mark up provider costs - you pay your providers directly at their standard rates. We show usage estimates based on token counts and model pricing, but your provider's invoices are the final authority. The Premium subscription only covers unlimited bot access and infrastructure, not provider API costs. For OkeyBot’s built‑in models, access may also depend on your Okey Credits balance."
    },
    {
      question: "Can I use multiple AI providers?",
      answer: "Yes! You can connect API keys from OpenAI, Anthropic, Google, xAI, Groq, Perplexity, and OpenRouter. Enable the models you want from each provider, then switch between them using /model. You can also craft 'Custom Models' in your dashboard to wrap any provider's model with a permanent persona or system instruction."
    },
    {
      question: "How do I switch models mid-conversation?",
      answer: "Simply use the /model command inside any thread created with /chat. Select a different model from the autocomplete list, and all future messages in that thread will use the new model. The conversation context carries over seamlessly."
    },
    {
      question: "What happens to my data if I delete my account?",
      answer: "When you delete your account in the dashboard, we permanently remove your API keys, preferences, user models, usage logs, thread settings, and user record. We may retain minimal billing information as required by law. Note that messages posted in Discord remain on Discord - deleting your OkeyBot account doesn't remove Discord content."
    },
    {
      question: "Does my whole server share one OkeyBot account?",
      answer: "It depends on the mode! In 'Thread Mode' (/chat), every user brings their own account, limits, and API keys. However, in 'Mention Mode' (when someone @mentions the bot in a server channel), the bot uses the Server Owner's configured API key and model. This allows server owners to craft a dedicated community assistant—complete with specialized instructions or a unique personality tailored to their server—while still letting members create private threads for their own tasks."
    },
    {
      question: "Can others use my API keys?",
      answer: "In /chat threads: By default, no. If you enable 'Allow others' in Settings, members can participate in threads you started, using your limits. In Mentions: If you assign a model to a server in your Mention Mode settings, anyone mentioning the bot in that server will use your API key and budget."
    },
    {
      question: "How do I track my usage and costs?",
      answer: "Use /usage in any Discord thread to see that thread's token counts and estimated costs. For detailed analytics, visit the Usage page in the dashboard where you can see breakdowns by provider, model, and time period. You can also view your provider's official dashboards for authoritative billing information."
    },
    {
      question: "Do you train AI models on my data?",
      answer: "No. We do not train any models on your data, and we do not store your conversation content in our database. Messages live in Discord per Discord's policies. Provider data policies vary - most major providers don't use API data for training by default, but you should review and configure your provider account settings."
    },
    {
      question: "Can I use vision models or image generation?",
      answer: "Yes! Models that support vision (like GPT-4 Vision or Claude Opus) can analyze images you attach in Discord threads. For image/video generation models, OkeyBot creates dedicated generation threads where you send prompts and receive generated media. Availability depends on your enabled providers."
    },
    {
      question: "What is the Memory feature and how do I set it up?",
      answer: "Memory lets OkeyBot automatically learn and remember details about you across your private threads and DMs. 30 memories. As they accumulate, it curates them by combining related ones, updating what's changed, and replacing what's no longer useful. It gets sharper over time, not just fuller. To set it up, go to Dashboard → Settings, toggle Memory on, select a server you own, and click 'Setup #okey-memory' to create the channel. You can also choose which AI model is used for background memory extraction (Auto selects an enabled model from your connected providers, then falls back to a built-in model). Once set up, the AI will naturally recall relevant information across conversations without being told again."
    },
    {
      question: "How do I cancel my Premium subscription?",
      answer: "Go to Dashboard → Settings → Billing and click 'Manage Billing' to access the Stripe customer portal. From there you can cancel your subscription anytime. Cancellation takes effect at the end of your current billing period - you'll retain Premium access until then."
    },
    {
      question: "What if I hit rate limits?",
      answer: "Rate limits are enforced by your provider, not OkeyBot. If you hit limits, check your provider's dashboard to see your current quotas. You may need to upgrade your plan with them or wait for limits to reset. Each provider has different rate limit policies. Separately, if you reach OkeyBot’s own message limits or run out of Okey Credits for built‑in models, you can either add your own keys or upgrade your OkeyBot plan."
    },
    {
      question: "Can I use OkeyBot in DMs or outside threads?",
      answer: "Yes! You can @mention the bot in any channel or DM it directly. In DMs, you don't need to mention - just send a message. To maintain context across turns, reply to the bot's last message. This creates a reply chain that gives the bot conversation history. Mention/DM mode uses your default model preference."
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="legal-page">
      {/* Navigation */}
      <SiteNav />

      {/* Header */}
      <div className="legal-header">
        <div className="legal-header-content">
          <h1 className="legal-title">Support Center</h1>
          <p className="legal-subtitle">We're here to help you get the most out of OkeyBot</p>
        </div>
      </div>

      {/* Content */}
      <div className="legal-container">
        {/* Contact Section */}
        <div style={{ marginBottom: "2rem" }}>
          <div className="support-contact-grid">
            {/* Email Support */}
            <div className="support-contact-card">
              <div className="support-contact-icon" style={{ background: "rgba(20, 184, 166, 0.1)" }}>
                <svg width={32} height={32} fill="var(--accent)" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <h3 className="support-contact-title">Email Support</h3>
              <p className="support-contact-description">
                Send us an email and we'll get back to you within 24 hours. Premium users get priority response.
              </p>
              <a href="mailto:contact@okeybot.ai" className="support-contact-button support-button-primary">
                <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                contact@okeybot.ai
              </a>
            </div>

            {/* Discord Support */}
            <div className="support-contact-card">
              <div className="support-contact-icon" style={{ background: "rgba(88, 101, 242, 0.1)" }}>
                <svg width={32} height={32} fill="#5865F2" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
              </div>
              <h3 className="support-contact-title">Discord Community</h3>
              <p className="support-contact-description">
                Join our Discord server for quick help from the community and our support team. Get real-time assistance!
              </p>
              <a href="https://discord.gg/WAgvT7QxWx" target="_blank" rel="noopener noreferrer" className="support-contact-button support-button-discord" data-umami-event="join_support_discord">
                <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
                Join Support Server
              </a>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="support-resources-card">
            <h3 className="support-resources-title">
              <svg width={20} height={20} fill="var(--accent)" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
              </svg>
              Quick Resources
            </h3>
            <div className="support-resources-grid">
              <button onClick={() => navigate("/docs")} className="support-resource-item">
                <svg width={20} height={20} fill="var(--accent)" viewBox="0 0 24 24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                </svg>
                <div>
                  <div className="support-resource-name">Documentation</div>
                  <div className="support-resource-desc">Complete guides</div>
                </div>
              </button>

              <button onClick={() => navigate("/dashboard")} className="support-resource-item">
                <svg width={20} height={20} fill="var(--accent)" viewBox="0 0 24 24">
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
                </svg>
                <div>
                  <div className="support-resource-name">Dashboard</div>
                  <div className="support-resource-desc">Manage settings</div>
                </div>
              </button>

              {/* Status Page - Uncomment when ready
              <a 
                href="https://status.okeybot.ai" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="support-resource-item"
              >
                <svg width={20} height={20} fill="var(--success)" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <div>
                  <div className="support-resource-name">Status Page</div>
                  <div className="support-resource-desc">System status</div>
                </div>
              </a>
              */}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="legal-content">
          <section className="legal-section">
            <h2 className="legal-section-title" style={{ marginBottom: "1.5rem" }}>
              Frequently Asked Questions
            </h2>

            {faqs.map((faq, index) => (
              <div key={index} className="support-faq-item">
                <button onClick={() => toggleFaq(index)} className="support-faq-question">
                  <span>{faq.question}</span>
                  <svg
                    width={20}
                    height={20}
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    className="support-faq-icon"
                    style={{ transform: expandedFaq === index ? "rotate(180deg)" : "rotate(0deg)" }}
                  >
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </button>
                {expandedFaq === index && (
                  <div className="support-faq-answer">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </section>

          {/* Still need help */}
          <section className="legal-section support-cta-section">
            <div className="support-cta-card">
              <svg width={48} height={48} fill="var(--accent)" viewBox="0 0 24 24" style={{ margin: "0 auto 1.5rem" }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
              </svg>
              <h3 className="support-cta-title">Still Need Help?</h3>
              <p className="support-cta-description">
                Can't find what you're looking for? Our support team is ready to help you get back on track.
              </p>
              <div className="support-cta-buttons">
                <a href="mailto:contact@okeybot.ai" className="support-cta-button support-cta-button-primary">
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  Email Support
                </a>
                <a href="https://discord.gg/WAgvT7QxWx" target="_blank" rel="noopener noreferrer" className="support-cta-button support-cta-button-secondary">
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
                  </svg>
                  Join Discord
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
